/**
 * KAN-678: строка над таблицей кампаний с окном отправки Telegram-рассылки.
 *
 * "Окно отправки Telegram: 10:00-19:00 МСК, пауза 60-120 с, лимит 5 новых
 * чатов в день" с кнопкой-карандашом (диалог правки). Второй строкой, если у
 * оператора есть канал voice: "Окно обзвона: 09:00-21:00 МСК, до 30 звонков в
 * час" (только чтение). Операторов несколько - выбор в диалоге; в строке
 * показывается имя выбранного.
 *
 * Данные и сохранение: hooks/useOutreachWindow. Без операторов с каналом
 * telegram_user бар не рендерится.
 */
import { useState } from 'react';
import { Clock, Pencil, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOutreachWindow } from '@/hooks/useOutreachWindow';
import { formatOutreachWindow, formatVoiceWindow } from '@/lib/outreachWindow';
import { OutreachWindowDialog } from './OutreachWindowDialog';

export const NO_EDIT_HINT = 'Нет прав менять настройки этого оператора';

export function OutreachWindowBar(): JSX.Element | null {
  const state = useOutreachWindow();
  const [open, setOpen] = useState(false);

  if (state.operators.length === 0 || state.selectedOperatorId === null) return null;

  const operator = state.operators.find((o) => o.id === state.selectedOperatorId);
  const operatorLabel = state.operators.length > 1 ? operator?.label : undefined;

  return (
    <div
      data-testid="outreach-window-bar"
      className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 mb-4 text-sm text-slate-700"
    >
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
        <span data-testid="outreach-window-line" className="flex-1 min-w-0">
          <span className="font-medium">Окно отправки Telegram: </span>
          {state.window ? formatOutreachWindow(state.window, state.timezone) : 'загрузка...'}
          {operatorLabel && (
            <span className="text-slate-500" data-testid="outreach-window-operator-label">
              {' '}
              ({operatorLabel})
            </span>
          )}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="outreach-window-edit"
          aria-label="Изменить окно отправки"
          title={state.canEdit ? 'Изменить окно отправки' : NO_EDIT_HINT}
          disabled={!state.canEdit || state.window === null}
          onClick={() => setOpen(true)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>

      {state.voiceWindow && (
        <div className="flex items-center gap-2" data-testid="voice-window-line">
          <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <span>
            <span className="font-medium">Окно обзвона: </span>
            {formatVoiceWindow(state.voiceWindow, state.timezone)}
          </span>
        </div>
      )}

      <OutreachWindowDialog
        open={open}
        onOpenChange={setOpen}
        operators={state.operators}
        selectedOperatorId={state.selectedOperatorId}
        onOperatorChange={state.setSelectedOperatorId}
        values={state.window}
        limitBounds={state.limitBounds}
        timezone={state.timezone}
        canEdit={state.canEdit}
        isSaving={state.isSaving}
        onSave={state.save}
      />
    </div>
  );
}

export default OutreachWindowBar;
